import Util from './Util';

class Time {
  public static readonly TIME_24_HOURS = Time.fromString('24:00');

  public static dateStringToObj(dateStr: string) {
    const dateArray = dateStr.split(':');

    const isNegative = dateArray[0].includes('-');
    const hours = Math.abs(parseInt(dateArray[0], 10)) || 0;
    const minutes = parseInt(dateArray[1], 10) || 0;
    const seconds = parseInt(dateArray[2], 10) || 0;

    return {
      isNegative,
      hours,
      minutes,
      seconds,
    };
  }

  public static dateStringToDate(dateStr: string) {
    const date = dateStr.split('/');
    const newDate = date[1] + '/' + date[0] + '/' + date[2];
    return new Date(newDate);
  }

  public static formatTime(timeStr: string): string {
    if (Util.isEmptyOrBlank(timeStr) || timeStr === '-') {
      return '00:00';
    }

    let hour = timeStr.split(':')[0] || '00';
    let minute = timeStr.split(':')[1] || '00';
    // let second = timeStr.split(':')[2] || '00';

    if (Number.parseInt(hour, 10) >= 24) {
      hour = '23';
      minute = '59';
      // second = '59';
    } else if (Number.parseInt(hour, 10) < 0) {
      hour = '00';
      minute = '00';
      // second = '00';
    }

    hour = hour.padStart(2, '0');
    minute = minute.padStart(2, '0');
    // second = second.padStart(2, '0');

    return `${hour}:${minute}`;
  }


  public static fromString(timeStr: string): Time {
    if (timeStr === '') {
      timeStr = '00:00';
    }

    const timeObj = Time.dateStringToObj(timeStr);

    let seconds = timeObj.seconds + timeObj.minutes * 60 + timeObj.hours * 60 * 60;

    if (timeObj.isNegative) {
      seconds *= -1;
    }

    return new Time(seconds);
  }

  public static fromCurrentTime(): Time {
    const currentDate = new Date();
    const secondsCurrentTime = currentDate.getHours()*60*60 + currentDate.getMinutes()*60 + currentDate.getSeconds();
    return new Time(secondsCurrentTime);
  }

  public static fromFloat(reference: number | string): Time {
    if (Util.isEmptyOrBlank(reference)) {
      return null;
    }

    if (typeof reference === 'string') {
      reference = parseFloat(reference);
    }

    // 1.0 = 1 hour
    const seconds = Math.floor(reference * 60 * 60);

    const time = new Time(seconds);

    return time;
  }

  public static compare(time1Str: string, time2Str: string): number {
    const time1 = Time.fromString(time1Str);
    const time2 = Time.fromString(time2Str);

    const difference = time1.getSeconds() - time2.getSeconds();

    if (difference > 0) {
      return 1;
    } else if (difference < 0) {
      return -1;
    }

    return 0;
  }

  public static min(
    timeA: Time,
    timeB: Time
  ): Time {
    return this.minOrMax(timeA, timeB, false);
  }

  public static max(
    timeA: Time,
    timeB: Time,
  ): Time {
    return this.minOrMax(timeA, timeB, true);
  }

  private static minOrMax(
    timeA: Time,
    timeB: Time,
    max = true,
  ): Time {
    const timeAStr = timeA.toString();
    const timeBStr = timeB.toString();

    const aIsGreater = this.compare(timeAStr, timeBStr) === 1;

    if (aIsGreater && max || !aIsGreater && !max) {
      return timeA;
    } else {
      return timeB;
    }
  }

  private seconds: number;

  constructor(seconds: number) {
    this.seconds = seconds;
  }

  public static sumArray(timeArray: Time[]) {
    const baseTime = this.fromString('00:00');

    const summedTime = timeArray.reduce((time, accumulator) =>
      time.sum(accumulator), baseTime);

    return summedTime;
  }

  public sum(timeToSum: Time): Time {
    const sum = this.seconds + timeToSum.seconds;

    return new Time(sum);
  }

  public subtract(timeToSub: Time): Time {
    const difference = this.seconds - timeToSub.seconds;

    return new Time(difference);
  }

  public getSeconds() {
    return this.seconds;
  }

  public toString(
    format: string = 'HH:MM',
    roundSeconds = false,
    ): string {
    // -1280 => 1280
    const secondsModule = Math.abs(this.seconds);

    const hours = Math.floor(secondsModule / (60 * 60));
    const minutesFloat = (secondsModule - hours * 60 * 60) / 60;

    const minutes = roundSeconds ?
      Math.round(minutesFloat) :
      Math.floor(minutesFloat);

    const seconds = secondsModule - hours * 60 * 60 - minutes * 60;

    // Para evitar que a string fique como '12:0:3' ao invés de '12:00:03'
    let hoursStr = hours.toString().padStart(2, '0');
    const minutesStr = minutes.toString().padStart(2, '0');
    const secondsStr = seconds.toString().padStart(2, '0');

    if (this.seconds < 0) {
      hoursStr = `-${hoursStr}`;
    }

    let timeString: string;

    switch (format) {
      case 'HH:MM':
        timeString = `${hoursStr}:${minutesStr}`;
        break;
      default:
        timeString = `${hoursStr}:${minutesStr}:${secondsStr}`;
    }

    return timeString;
  }

  public toObject() {
    const timeString = this.toString();
    const timeObject = Time.dateStringToObj(timeString);

    return timeObject;
  }

  public toFloat(): number {
    const hours = this.seconds / (60 * 60);

    return hours;
  }
}

export default Time;
